/* Middleware checkout compatibility for Maximize. Native cart/product handlers remain intact. */
(() => {
  'use strict';
  if (window.MBGCartGuard) return;
  const cartScope = '#CartDrawer, #MainCart__Items, #main-cart, .main-cart__custom-accordion';
  const checkoutSelector = '#MainCart__Checkout, #checkout[name="checkout"], #MainCart__Checkout + .mw-btn-modal, #checkout + .mw-btn-modal, .mw-btn-modal.main-cart__checkout-button, .mw-btn-modal.cart-drawer__checkout';
  const replay = new WeakSet();
  let pending = 0, settleUntil = 0, failed = false, opening = false;
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
  const alpineData = element => {
    try { return element && window.Alpine?.$data(element); } catch (_) { return null; }
  };
  const helperFor = scope => scope === 'page' ? window.Alpine?.store('xCartHelper') : window.xCartHelper;
  const scopeFor = element => element?.matches('#MainCart__Checkout, #MainCart__Checkout + .mw-btn-modal, .main-cart__checkout-button, #main-cart') ? 'page' : 'drawer';
  const originalFor = scope => document.querySelector(scope === 'page' ? '#MainCart__Checkout' : '#checkout[name="checkout"]');

  // Maximize's helpers do not consistently expose pending requests. Observe only cart writes;
  // return the original fetch promise and response without consuming or altering either.
  const nativeFetch = window.fetch;
  window.fetch = function (input, options) {
    const url = typeof input === 'string' || input instanceof URL ? String(input) : input?.url;
    const method = options?.method || input?.method || 'GET';
    const mutation = method.toUpperCase() !== 'GET' && /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/.test(url || '');
    const result = nativeFetch.apply(this, arguments);
    if (mutation) {
      pending++;
      result.then(response => { failed = !response.ok; }, () => { failed = true; })
        .then(() => {
          pending--;
          settleUntil = Math.max(settleUntil, Date.now() + 100);
          window.MbgInstallationNote?.schedule();
        });
    }
    return result;
  };

  function noteEdit(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest(cartScope) && !target.matches('#x-cart-note, #MainCart__CustomField')) return;
    const quantity = target.closest('.cart-item__quantity, .cart-item__remove, [data-action="remove-item"]');
    if (event.type === 'click' && !quantity) return;
    if (!quantity && !target.matches('input:not([type="checkbox"]), textarea, select')) return;
    // Covers the native 500ms update and the B2B 2s quantity validation debounce.
    settleUntil = Date.now() + (quantity ? 2100 : 1100);
  }
  ['input', 'change', 'click'].forEach(type => document.addEventListener(type, noteEdit, true));

  function isPending() {
    return pending > 0 || Date.now() < settleUntil || !!window.xCartHelper?.isUpdating ||
      !!document.querySelector('#CartDrawer')?.isLoading ||
      !!alpineData(document.querySelector('#MainCart__Items'))?.isLoading ||
      ['page', 'drawer'].some(scope => originalFor(scope)?.getAttribute('aria-disabled') === 'true');
  }
  async function waitForIdle() {
    const deadline = Date.now() + 12000;
    while (isPending() && Date.now() < deadline) await sleep(80);
    return !isPending() && !failed;
  }
  function validate(scope = window.Maximize?.isCartPage ? 'page' : 'drawer') {
    const helper = helperFor(scope);
    if (!helper?.validateCart) return false;
    helper.validateCart();
    const agreement = document.querySelector(scope === 'page' ? '#MainCart__Agree' : '#CartDrawer__Agree');
    if (agreement && !agreement.checked) { agreement.focus(); return false; }
    if (!helper.validated) return false;
    const form = document.getElementById(scope === 'page' ? 'main-cart' : 'CartDrawerForm');
    return !form || form.reportValidity();
  }
  window.MBGCartGuard = Object.freeze({ isPending, waitForIdle, validate });

  async function prepareInstallationNote(element) {
    const note = window.MbgInstallationNote;
    if (!note) return true;
    const scope = element?.closest('#mw-modal, .mbg-tbi-container, .product-info__form-wrapper, #CartDrawer, .section-main-cart') || element?.parentElement;
    let error = scope?.querySelector('[data-mbg-installation-note-error]');
    try {
      await note.ensure();
      note.refreshModal();
      if (error) error.hidden = true;
      return true;
    } catch (_) {
      if (scope && !error) {
        error = document.createElement('p');
        error.dataset.mbgInstallationNoteError = '';
        error.className = 'flash-message error-message';
        error.setAttribute('role', 'alert');
        scope.appendChild(error);
      }
      if (error) {
        const config = JSON.parse(document.getElementById('MbgInstallationNoteConfig')?.textContent || '{}');
        error.textContent = config.error;
        error.hidden = false;
      }
      return false;
    }
  }

  function productState(clone) {
    const form = clone.closest('form[action*="/cart/add"]');
    const button = form?.querySelector('[name="add"]');
    const wrapper = form?.closest('.product-info__form-wrapper, [x-data^="xProductForm"]');
    const state = alpineData(wrapper);
    const variant = form?.elements.namedItem('id');
    const dynamic = form?.querySelector('.product-form__dynamic-checkout');
    const hidden = !!dynamic && (dynamic.classList.contains('hidden') || dynamic.hasAttribute('x-cloak'));
    const disabled = !form || !variant?.value || variant.disabled || !button || button.disabled ||
      button.getAttribute('aria-disabled') === 'true' || state?.isAvailableButton === false ||
      state?.isOptionExist === false || !!state?.isLoading || hidden;
    return { form, state, hidden, disabled };
  }
  function refreshClones() {
    document.querySelectorAll('.mw-btn-modal, .mw-add-btn').forEach(clone => {
      if (!clone.matches(checkoutSelector) && !clone.classList.contains('mw-add-btn')) return;
      const product = clone.classList.contains('mw-add-btn');
      const native = product ? null : originalFor(scopeFor(clone));
      const state = product ? productState(clone) : null;
      const disabled = product ? state.disabled : !native || native.disabled || native.getAttribute('aria-disabled') === 'true';
      clone.setAttribute('role', 'button');
      clone.setAttribute('tabindex', disabled ? '-1' : '0');
      clone.setAttribute('aria-disabled', String(!!disabled));
      if (clone.tagName === 'BUTTON') { clone.type = 'button'; clone.disabled = !!disabled; }
      if (product) {
        clone.hidden = !!state.hidden;
        if (!disabled) clone.removeAttribute('disabled');
      } else if (native && !clone.dataset.mbgLabel) {
        clone.className = `${native.className} mw-btn-modal`;
        // Preserve Maximize's existing label/icon treatment, avoiding duplicate IDs or Alpine bindings.
        const label = native.cloneNode(true);
        label.querySelectorAll('*').forEach(node => {
          Array.from(node.attributes).forEach(attribute => {
            if (/^(id$|x-|:|@|on)/.test(attribute.name)) node.removeAttribute(attribute.name);
          });
        });
        clone.replaceChildren(...label.childNodes);
        clone.dataset.mbgLabel = 'true';
        if (native.getAttribute('aria-label')) clone.setAttribute('aria-label', native.getAttribute('aria-label'));
      }
    });
  }
  function replayClick(element) {
    replay.add(element);
    try { element.click(); } finally { replay.delete(element); }
  }
  let cartRefreshRevision = 0;
  async function refreshNativeCart() {
    const revision = ++cartRefreshRevision;
    try {
      const helper = window.xCartHelper;
      const drawer = document.getElementById('CartDrawer');
      const sectionId = window.Maximize?.isCartPage ? document.getElementById('MainCart__Items')?.dataset.sectionId : drawer?.dataset.sectionId;
      if (!helper?.getSectionsToRender || !sectionId) return;
      const sections = helper.getSectionsToRender(sectionId).filter(section => document.querySelector(section.selector));
      if (drawer?.dataset.sectionId && !sections.some(section => section.selector === '#CartDrawer')) {
        sections.push({ id: drawer.dataset.sectionId, selector: '#CartDrawer' });
      }
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', [...new Set(sections.map(section => section.id))].join(','));
      const response = await fetch(url.href);
      if (!response.ok) return;
      const rendered = await response.json();
      if (revision !== cartRefreshRevision) return;
      sections.forEach(section => {
        const element = document.querySelector(section.selector);
        if (element && rendered[section.id]) {
          const drawerWasOpen = section.selector === '#CartDrawer' && element.hasAttribute('open');
          const hadDrawerFocus = drawerWasOpen && element.contains(document.activeElement);
          const focusedId = hadDrawerFocus ? document.activeElement.id : '';
          element.innerHTML = getSectionInnerHTML(rendered[section.id], section.selector);
          if (drawerWasOpen) {
            element.querySelector('#CartDrawer__Wrapper')?.classList.add('is-visible');
            if (hadDrawerFocus) {
              const focusId = focusedId && element.contains(document.getElementById(focusedId)) ? focusedId : 'CloseCart';
              const focusHelper = window.Alpine?.store('xFocusElement');
              if (focusHelper) focusHelper.trapFocus('CartDrawer__Wrapper', focusId);
              else document.getElementById(focusId)?.focus({ preventScroll: true });
            }
          }
        }
      });
      helper.updateCurrentCountItem();
      const pageHelper = window.Alpine?.store('xCartHelper');
      if (pageHelper) pageHelper.currentItemCount = helper.currentItemCount;
      // Keep the drawer closed behind Middleware; native custom elements initialize their new children.
      if (typeof CART_EVENT !== 'undefined') document.dispatchEvent(new CustomEvent(CART_EVENT.cartUpdate, {
        detail: { isItemCountChanged: true, openMiniCart: false }
      }));
    } catch (error) { console.warn('Unable to refresh the cart after Middleware checkout.', error); }
  }
  async function checkout(element, scope) {
    if (opening) return;
    opening = true;
    element?.setAttribute('aria-busy', 'true');
    try {
      if (!(await waitForIdle()) || !validate(scope)) return;
      const helper = helperFor(scope);
      const checkEvent = { defaultPrevented: false, preventDefault() { this.defaultPrevented = true; } };
      // Preserve native terms/custom-field validation AND the theme's internal attribute cleanup.
      helper.goToCheckout(checkEvent);
      if (checkEvent.defaultPrevented || !helper.validated || !(await waitForIdle()) || !validate(scope)) return;
      if (!(await prepareInstallationNote(element))) return;
      let original = originalFor(scope);
      if (!original || original.disabled) return;
      // A native re-render may have replaced the button while its cart request was settling.
      for (let tries = 0; !original.nextElementSibling?.classList.contains('mw-btn-modal') && tries < 10; tries++) {
        await sleep(80);
        original = originalFor(scope);
        if (!original) return;
      }
      const target = original.nextElementSibling?.classList.contains('mw-btn-modal') ? original.nextElementSibling : original;
      if (target.getAttribute('aria-disabled') !== 'true') replayClick(target);
    } finally {
      opening = false;
      element?.removeAttribute('aria-busy');
    }
  }
  async function buyNow(clone) {
    if (opening) return;
    const initial = productState(clone);
    if (initial.disabled || !initial.form.reportValidity()) return;
    if (initial.state?.__validateQuantityBeforeAdd && !initial.state.__validateQuantityBeforeAdd()) return;
    const invalidOption = initial.form.closest('.product-template__wrapper')?.querySelector('.customizable-option.validate-checkbox, .customizable-option:required:invalid');
    if (invalidOption) { invalidOption.reportValidity(); invalidOption.focus(); return; }
    opening = true;
    clone.setAttribute('aria-busy', 'true');
    try {
      if (!(await waitForIdle())) return;
      const current = productState(clone);
      if (!clone.isConnected || current.disabled || !current.form.reportValidity()) return;
      // Vendor serializes THIS form, including its current variant, quantity and product properties.
      replayClick(clone);
      // Prevent a double add while the vendor's jQuery request opens its checkout dialog.
      const deadline = Date.now() + 10000;
      while (Date.now() < deadline) {
        const modal = document.getElementById('mw-modal');
        if (modal && modal.getClientRects().length) {
          await refreshNativeCart();
          await prepareInstallationNote(clone);
          break;
        }
        await sleep(100);
      }
    } finally {
      opening = false;
      clone.removeAttribute('aria-busy');
    }
  }
  async function completeMiddlewareOrder(element) {
    if (opening) return;
    opening = true;
    element.setAttribute('aria-busy', 'true');
    try {
      if (!(await waitForIdle()) || !(await prepareInstallationNote(element))) return;
      if (element.isConnected && !element.disabled) replayClick(element);
    } finally {
      opening = false;
      element.removeAttribute('aria-busy');
    }
  }
  document.addEventListener('click', event => {
    const element = event.target instanceof Element ? event.target.closest(`${checkoutSelector}, .mw-add-btn, #mw-modal .mw-complete-button`) : null;
    if (!element || replay.has(element)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (element.classList.contains('mw-complete-button')) void completeMiddlewareOrder(element);
    else if (element.classList.contains('mw-add-btn')) void buyNow(element);
    else void checkout(element, scopeFor(element));
  }, true);
  document.addEventListener('submit', event => {
    if (!event.target.matches('#main-cart, #CartDrawerForm')) return;
    if (event.submitter && replay.has(event.submitter)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const scope = scopeFor(event.target);
    void checkout(originalFor(scope), scope);
  }, true);
  document.addEventListener('keydown', event => {
    const element = event.target.closest('.mw-btn-modal, .mw-add-btn');
    if (!element || !element.matches(`${checkoutSelector}, .mw-add-btn`) || element.tagName === 'BUTTON') return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (element.getAttribute('aria-disabled') !== 'true') element.click();
    }
  });
  let refreshQueued = false;
  let modalWasOpen = false;
  let noteAjaxTrackingInstalled = false;
  async function refreshAfterModalClose() {
    // Middleware changes cart quantities through jQuery rather than Maximize's fetch helpers.
    const deadline = Date.now() + 10000;
    await sleep(250);
    while (window.jQuery?.active && Date.now() < deadline) await sleep(100);
    await refreshNativeCart();
  }
  const observe = () => {
    new MutationObserver(() => {
      if (refreshQueued) return;
      refreshQueued = true;
      requestAnimationFrame(() => {
        refreshQueued = false;
        refreshClones();
        window.MbgInstallationNote?.refreshModal();
      });
    }).observe(document.body, { childList: true, subtree: true });
    refreshClones();
    // Variant availability is also updated in-place by Alpine rather than via a DOM replacement.
    setInterval(() => {
      refreshClones();
      if (!noteAjaxTrackingInstalled && window.jQuery) {
        noteAjaxTrackingInstalled = true;
        window.jQuery(document).on('ajaxComplete.mbgInstallationNote', (_event, _xhr, settings) => {
          if ((settings.type || 'GET').toUpperCase() !== 'GET' && /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/.test(settings.url || '')) {
            window.MbgInstallationNote?.schedule();
          }
        });
      }
      const modal = document.getElementById('mw-modal');
      const modalIsOpen = !!modal?.getClientRects().length;
      if (modalWasOpen && !modalIsOpen) void refreshAfterModalClose();
      modalWasOpen = modalIsOpen;
    }, 400);
  };
  if (document.body) observe();
  else document.addEventListener('DOMContentLoaded', observe, { once: true });
})();
